import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { User } from '../../services/authService';
import { Role, usersService, CreateUserRequest, UpdateUserRequest } from '../../services/usersService';

interface UserFormProps {
  user?: User;
  roles: Role[];
  onSubmit: () => void;
  onCancel: () => void;
  isEdit?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  roles,
  onSubmit,
  onCancel,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    password: '',
    confirmPassword: '',
    role_id: user?.role?.id || '',
    is_active: user?.is_active ?? true,
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.username.trim()) {
      setError('El nombre de usuario es requerido');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('El email es requerido');
      return false;
    }
    
    if (!isEdit && !formData.password) {
      setError('La contraseña es requerida');
      return false;
    }
    
    if (!isEdit && formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }
    
    if (formData.password && formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Formato de email inválido');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      if (isEdit && user) {
        // Update user
        const updateData: UpdateUserRequest = {
          username: formData.username,
          email: formData.email,
          is_active: formData.is_active,
          role_id: formData.role_id ? Number(formData.role_id) : undefined,
        };
        
        await usersService.updateUser(user.id, updateData);
      } else {
        // Create user
        const createData: CreateUserRequest = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role_id: formData.role_id ? Number(formData.role_id) : undefined,
        };
        
        await usersService.createUser(createData);
      }
      
      onSubmit();
    } catch (err: any) {
      console.error('Form submission error:', err);
      
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError(isEdit ? 'Error al actualizar usuario' : 'Error al crear usuario');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <TextField
        name="username"
        label="Nombre de Usuario"
        value={formData.username}
        onChange={handleInputChange}
        fullWidth
        required
        margin="normal"
        disabled={loading}
      />
      
      <TextField
        name="email"
        label="Email"
        type="email"
        value={formData.email}
        onChange={handleInputChange}
        fullWidth
        required
        margin="normal"
        disabled={loading}
      />
      
      <FormControl fullWidth margin="normal">
        <InputLabel>Rol</InputLabel>
        <Select
          name="role_id"
          value={formData.role_id}
          onChange={handleSelectChange}
          label="Rol"
          disabled={loading}
        >
          <MenuItem value="">Sin rol asignado</MenuItem>
          {roles.map((role) => (
            <MenuItem key={role.id} value={role.id}>
              {role.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {!isEdit && (
        <>
          <TextField
            name="password"
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            fullWidth
            required
            margin="normal"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            name="confirmPassword"
            label="Confirmar Contraseña"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            fullWidth
            required
            margin="normal"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </>
      )}
      
      {isEdit && (
        <FormControlLabel
          control={
            <Switch
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
              disabled={loading}
            />
          }
          label="Usuario Activo"
          sx={{ mt: 2 }}
        />
      )}
      
      <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          className="arquitectura-gradient"
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            isEdit ? 'Actualizar' : 'Crear'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default UserForm;